import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { Button, Input, Switch } from '@/components/ui';
import { AuthHeading, FormMessage } from './AuthLayout';
import { PasswordField } from './PasswordField';
import { useAuthContext } from './useAuth';
import { cognitoResendCode, cognitoSignIn, friendlyAuthError } from './cognito';
import { devSignIn } from './devAuth';

export default function SignInPage() {
  const { mode, refresh, notice, clearNotice } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as { email?: string; confirmed?: boolean; reset?: boolean };
  const [email, setEmail] = useState(state.email ?? '');
  const [password, setPassword] = useState('');
  const [admin, setAdmin] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const info = state.confirmed
    ? 'Email confirmed. You can sign in now.'
    : state.reset
      ? 'Password updated. Sign in with your new password.'
      : null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    clearNotice();
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) return setError('Enter your email address.');
    setBusy(true);
    try {
      if (mode === 'dev') {
        devSignIn({ email: cleanEmail, role: admin ? 'admin' : 'user' });
        await refresh();
        return;
      }
      if (!password) return setError('Enter your password.');
      const outcome = await cognitoSignIn(cleanEmail, password);
      if (outcome === 'done') {
        await refresh();
      } else if (outcome === 'confirm_sign_up') {
        await cognitoResendCode(cleanEmail).catch(() => undefined);
        navigate(`/confirm?email=${encodeURIComponent(cleanEmail)}`, { state: { resent: true } });
      } else if (outcome === 'reset_password') {
        navigate(`/forgot?email=${encodeURIComponent(cleanEmail)}`);
      } else {
        setError('This account needs an extra step that is not supported here yet. Please contact support.');
      }
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <AuthHeading
        title={mode === 'dev' ? 'Developer sign in' : 'Welcome back'}
        subtitle={mode === 'dev' ? 'Local mode. No password needed, just an email.' : 'Sign in to continue your conversations.'}
      />
      <FormMessage error={error} notice={notice ?? info} />
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <Input
          label="Email"
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          autoFocus
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
        />
        {mode === 'dev' ? (
          <Switch checked={admin} onCheckedChange={setAdmin} label="Sign in as admin" description="Unlocks the admin dashboard" />
        ) : (
          <div>
            <PasswordField
              label="Password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <div className="mt-2 text-right">
              <Link to={`/forgot${email ? `?email=${encodeURIComponent(email)}` : ''}`} className="text-sm font-medium text-link hover:underline">
                Forgot password?
              </Link>
            </div>
          </div>
        )}
        <Button type="submit" size="lg" className="w-full" loading={busy}>
          {busy ? 'Signing in' : 'Sign in'}
        </Button>
      </form>
      {mode === 'cognito' && (
        <p className="mt-6 text-center text-sm text-ink-muted">
          New to MedicAiL?{' '}
          <Link to="/signup" className="font-medium text-link hover:underline">
            Create an account
          </Link>
        </p>
      )}
    </>
  );
}
